import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Container,
    Typography,
    Card,
    CardHeader,
    CardContent,
    Grid,
    Button,
    Stack,
    Box,
    Divider,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle
} from '@mui/material';
import Iconify from 'src/components/Iconify';
import Page from 'src/components/Page';
import Loader from 'src/components/Loader';
import Label from 'src/components/Label';
import { statusToColor } from 'src/components/Status';
import communication from 'src/constants/Communication';
import { authenticatedInstance } from 'src/utils/axiosInstance';
import { toast } from 'react-toastify';

export default function CertificateView() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [certificate, setCertificate] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [publishLoading, setPublishLoading] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [downloadLoading, setDownloadLoading] = useState(false);
    const [emailLoading, setEmailLoading] = useState(false);
    const [imageModalOpen, setImageModalOpen] = useState(false);

    useEffect(() => {
        const fetchCertificate = async () => {
            try {
                const response = await authenticatedInstance.get(`${communication.certificates}/${id}`);
                setCertificate(response.data);
            } catch (err) {
                console.error('Error fetching certificate:', err);
                setError('Failed to load certificate details.');
            } finally {
                setLoading(false);
            }
        };

        fetchCertificate();
    }, [id]);

    const handlePublish = async () => {
        setPublishLoading(true);
        try {
            let url;
            if (certificate.status === 'published') {
                url = communication.unpublishCertificate(id);
            } else {
                url = communication.publishCertificate(id);
            }
            const response = await authenticatedInstance.post(url);

            if (response.status === 200) {
                toast.success(`Certificate ${certificate.status === 'published' ? 'unpublished' : 'published'} successfully`);
            }

            // Update local state
            setCertificate({
                ...certificate,
                status: certificate.status === 'published' ? 'unpublished' : 'published'
            });


        } catch (err) {
            console.error('Error updating certificate status:', err);
        } finally {
            setPublishLoading(false);
        }
    };

    const handleDelete = async () => {
        try {
            await authenticatedInstance.delete(`${communication.certificates}/${id}`);
            toast.success('Certificate deleted successfully');
            navigate('/certificates');
        } catch (err) {
            console.error('Error deleting certificate:', err);
            toast.error('Failed to delete certificate.');
        } finally {
            setDeleteDialogOpen(false);
        }
    };

    const handleDownload = async () => {
        setDownloadLoading(true);
        try {
            const downloadUrl = communication.downloadCertificate(id);
            const response = await authenticatedInstance.get(downloadUrl, {
                responseType: 'blob'
            });

            // Create a blob URL and trigger download
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `certificate-${id}.pdf`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            toast.success('Certificate downloaded successfully');
        } catch (err) {
            console.error('Error downloading certificate:', err);
            toast.error('Failed to download certificate');
        } finally {
            setDownloadLoading(false);
        }
    };

    const handleEmailCertificate = async () => {
        setEmailLoading(true);
        try {
            // Assuming there's an endpoint for emailing certificates
            const url = communication.emailCertificate(id);
            const response = await authenticatedInstance.post(url);

            if (response.status === 200) {
                toast.success('Certificate emailed successfully to user');
            }
        } catch (err) {
            console.error('Error emailing certificate:', err);
            toast.error('Failed to email certificate');
        } finally {
            setEmailLoading(false);
        }
    };

    const handleOpenImageModal = () => {
        setImageModalOpen(true);
    };

    const handleCloseImageModal = () => {
        setImageModalOpen(false);
    };

    if (loading) {
        return <Loader />;
    }

    if (error) {
        return (
            <Page title="Certificate Error">
                <Container>
                    <Typography variant="h4" color="error" sx={{ mb: 5 }}>
                        {error}
                    </Typography>
                    <Button
                        variant="contained"
                        onClick={() => navigate('/certificates')}
                        startIcon={<Iconify icon="eva:arrow-back-fill" />}
                    >
                        Back to Certificates
                    </Button>
                </Container>
            </Page>
        );
    }

    return (
        <Page title={`Certificate - ${certificate?.course || ''}`}>
            <Container>
                <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
                    <Typography variant="h4" gutterBottom>
                        Certificate Details
                    </Typography>
                    <Box>
                        <Button
                            variant="contained"
                            color={certificate?.status === 'published' ? 'warning' : 'success'}
                            startIcon={<Iconify icon={certificate?.status === 'published' ? 'eva:eye-off-fill' : 'eva:eye-fill'} />}
                            onClick={handlePublish}
                            disabled={publishLoading || certificate?.status === 'unverified'}
                            sx={{ mr: 1 }}
                        >
                            {publishLoading ? (
                                <CircularProgress size={24} color="inherit" />
                            ) : (
                                certificate?.status === 'published' ? 'Unpublish' : 'Publish'
                            )}
                        </Button>
                        <Button
                            variant="contained"
                            color="info"
                            startIcon={<Iconify icon="eva:download-outline" />}
                            onClick={handleDownload}
                            disabled={downloadLoading}
                            sx={{ mr: 1 }}
                        >
                            {downloadLoading ? (
                                <CircularProgress size={24} color="inherit" />
                            ) : (
                                'Download'
                            )}
                        </Button>
                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={<Iconify icon="eva:email-outline" />}
                            onClick={handleEmailCertificate}
                            disabled={emailLoading}
                            sx={{ mr: 1 }}
                        >
                            {emailLoading ? (
                                <CircularProgress size={24} color="inherit" />
                            ) : (
                                'Email to User'
                            )}
                        </Button>
                        <Button
                            variant="contained"
                            color="error"
                            startIcon={<Iconify icon="eva:trash-2-fill" />}
                            onClick={() => setDeleteDialogOpen(true)}
                        >
                            Delete
                        </Button>
                    </Box>
                </Stack>

                <Card>
                    <CardHeader
                        title="Certificate Information"
                        action={
                            <Label variant="ghost" color={statusToColor(certificate?.status || 'unverified')}>
                                {certificate?.status || 'unverified'}
                            </Label>
                        }
                    />
                    <Divider />
                    <CardContent>
                        <Grid container spacing={3}>
                            <Grid item xs={12} md={6}>
                                <Typography variant="subtitle1">Issued To</Typography>
                                <Typography variant="body1">
                                  {certificate?.registration
                                    ? `${certificate.registration.firstName || ''} ${certificate.registration.lastName || ''}`.trim() || 'N/A'
                                    : 'N/A'}
                                </Typography>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Typography variant="subtitle1">Alternate Name</Typography>
                                <Typography variant="body1">{certificate?.alternateName || 'N/A'}</Typography>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Typography variant="subtitle1">Email</Typography>
                                <Typography variant="body1">{certificate?.registration?.email || certificate?.registration?.alternateEmail || 'N/A'}</Typography>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Typography variant="subtitle1">Course</Typography>
                                <Typography variant="body1">{certificate?.course || 'N/A'}</Typography>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Typography variant="subtitle1">Duration</Typography>
                                <Typography variant="body1">{certificate?.metadata?.duration || 'N/A'} hours</Typography>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Typography variant="subtitle1">Issue Date</Typography>
                                <Typography variant="body1">
                                    {certificate?.issueDate ? new Date(certificate.issueDate).toLocaleDateString() : 'N/A'}
                                </Typography>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Typography variant="subtitle1">Expiry Date</Typography>
                                <Typography variant="body1">
                                    {certificate?.expiryDate ? new Date(certificate.expiryDate).toLocaleDateString() : 'N/A'}
                                </Typography>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Typography variant="subtitle1">Template Used</Typography>
                                <Typography variant="body1">{certificate?.templateUsed || 'N/A'}</Typography>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Typography variant="subtitle1">Certificate ID</Typography>
                                <Typography variant="body1">{certificate?.id || 'N/A'}</Typography>
                            </Grid>
                        </Grid>

                        {certificate?.metadata?.contentfulProfileImage && (
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'start', mt: 4 }}>
                                <Typography variant="subtitle1" sx={{ mb: 1 }}>Uploaded Picture</Typography>
                                <Box
                                    component="img"
                                    src={certificate.metadata.contentfulProfileImage}
                                    alt="Student"
                                    sx={{
                                        width: '100%',
                                        maxWidth: 300,
                                        height: 'auto',
                                        borderRadius: 1,
                                        boxShadow: 2,
                                        cursor: 'pointer',
                                        '&:hover': {
                                            opacity: 0.9,
                                            boxShadow: 4
                                        }
                                    }}
                                    onClick={handleOpenImageModal}
                                />
                            </Box>
                        )}
                    </CardContent>
                </Card>

                <Box sx={{ mt: 3 }}>
                    <Button
                        variant="contained"
                        color="inherit"
                        onClick={() => navigate('/certificates')}
                        startIcon={<Iconify icon="eva:arrow-back-fill" />}
                    >
                        Back to Certificates
                    </Button>
                </Box>

                {/* Delete Confirmation Dialog */}
                <Dialog
                    open={deleteDialogOpen}
                    onClose={() => setDeleteDialogOpen(false)}
                >
                    <DialogTitle>Delete Certificate</DialogTitle>
                    <DialogContent>
                        <DialogContentText>
                            Are you sure you want to delete this certificate? This action cannot be undone.
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setDeleteDialogOpen(false)} color="inherit">
                            Cancel
                        </Button>
                        <Button onClick={handleDelete} color="error" variant="contained">
                            Delete
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Image Modal */}
                <Dialog
                    open={imageModalOpen}
                    onClose={handleCloseImageModal}
                    maxWidth="lg"
                    fullWidth
                >
                    <DialogContent sx={{ p: 0, position: 'relative' }}>
                        {certificate?.metadata?.contentfulProfileImage && (
                            <Box
                                component="img"
                                src={certificate.metadata.contentfulProfileImage}
                                alt="Student"
                                sx={{
                                    width: '100%',
                                    height: 'auto',
                                }}
                            />
                        )}
                        <Button
                            variant="contained"
                            color="primary"
                            sx={{ position: 'absolute', top: 8, right: 8 }}
                            onClick={handleCloseImageModal}
                        >
                            Close
                        </Button>
                    </DialogContent>
                </Dialog>
            </Container>
        </Page>
    );
}
